import { useCallback, useEffect, useRef, useState } from "react";
import { crmPost, CRM_FOLLOWUPS_CHANGED_EVENT, type CrmPage } from "./crm";
import {
  listGateInstances,
  subscribeGateInstancesChanged,
  type GateInstanceListOutcome,
} from "./gateInstancesClient";
import type { useNotificationSnapshots, NotificationRead } from "./notificationObserver";
import type { CrmReminder } from "./notifications";

import { useNeucleosWorldStore } from "./worldStore";

type ReminderRead = { ok: true; result: CrmPage<CrmReminder> } | { ok: false; reason: string };
export interface WorldNotificationSources {
  worldKey: string;
  gates: GateInstanceListOutcome;
  reminders: ReminderRead;
}

export async function readNotificationSources(
  worldKey: string,
  history: boolean,
): Promise<WorldNotificationSources> {
  const [gates, reminders] = await Promise.all([
    listGateInstances(worldKey),
    crmPost<CrmPage<CrmReminder>>(worldKey, {
      action: "followUpReminders",
      ...(history ? {} : { state: "delivered" }),
      paginationOpts: { numItems: 20, cursor: null },
    }).then(
      (result): ReminderRead => ({ ok: true, result }),
      (cause): ReminderRead => ({
        ok: false,
        reason: cause instanceof Error ? cause.message : "Reminders could not be read.",
      }),
    ),
  ]);
  return { worldKey, gates, reminders };
}

/** Capture all delivered pages before writing any receipts. Descending delivery
 * cursors do not chase new arrivals at the head while older pages are read. */
export async function captureNotificationReads(worldKey: string): Promise<NotificationRead[]> {
  const source = await readNotificationSources(worldKey, false);
  if (!source.gates.ok) throw new Error(source.gates.reason);
  if (!source.reminders.ok) throw new Error(source.reminders.reason);
  const rows: NotificationRead[] = source.gates.rows
    .filter((row) => (row.notificationReadState ?? "unread") === "unread")
    .map((row) => ({
      kind: "gate",
      id: row.gateInstanceKey,
      revision: row.targetDigest,
      state: "read",
    }));
  let page = source.reminders.result;
  const cursors = new Set<string>();
  for (;;) {
    for (const row of page.page)
      if (
        row.state === "delivered" &&
        !row.completedAt &&
        row.notificationRevision &&
        (row.notificationReadState ?? "unread") === "unread"
      )
        rows.push({
          kind: "reminder",
          id: row.reminderId,
          revision: row.notificationRevision,
          state: "read",
        });
    if (page.isDone) return rows;
    if (!page.continueCursor || cursors.has(page.continueCursor))
      throw new Error("Reminder pagination did not advance. Try again.");
    cursors.add(page.continueCursor);
    page = await crmPost<CrmPage<CrmReminder>>(worldKey, {
      action: "followUpReminders",
      state: "delivered",
      paginationOpts: { numItems: 20, cursor: page.continueCursor },
    });
  }
}

/** The compact feed invalidates only its World's opened sources. A refusal is
 * part of the render key, so neither cached pages nor late reads can survive it. */
export function useNotificationSources(
  worldKeys: readonly string[],
  open: boolean,
  history: boolean,
  feeds?: ReturnType<typeof useNotificationSnapshots>,
) {
  const grantRevision = useNeucleosWorldStore((state) => state.grantRevision);
  const identity = useNeucleosWorldStore((state) => state.identity);
  // Server renders have no document; treat the page as visible and let the
  // effect read the real visibility once the client mounts.
  const [visible, setVisible] = useState(() =>
    typeof document === "undefined" ? true : document.visibilityState !== "hidden",
  );
  useEffect(() => {
    const change = () => setVisible(document.visibilityState !== "hidden");
    change();
    document.addEventListener("visibilitychange", change);
    return () => document.removeEventListener("visibilitychange", change);
  }, []);
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const requests = worldKeys.map((worldKey) => {
    const feed = feeds?.find((row) => row.worldKey === worldKey);
    const admitted = feeds === undefined || Boolean(feed?.value);
    return {
      worldKey,
      admitted,
      key: JSON.stringify([
        worldKey,
        history,
        grantRevision,
        identity,
        revision,
        feed?.value?.gates,
        feed?.value?.reminders,
        feed?.error,
        admitted,
      ]),
    };
  });
  const signature = JSON.stringify(requests);
  const [snapshots, setSnapshots] = useState<
    Record<string, { key: string; row: WorldNotificationSources }>
  >({});
  const cache = useRef(snapshots);
  const generation = useRef(0);
  const pendingPages = useRef(new Set<string>());
  const [morePending, setMorePending] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    window.addEventListener(CRM_FOLLOWUPS_CHANGED_EVENT, refresh);
    const stop = subscribeGateInstancesChanged(refresh);
    return () => {
      window.removeEventListener(CRM_FOLLOWUPS_CHANGED_EVENT, refresh);
      stop();
    };
  }, [refresh]);
  useEffect(() => {
    const cut = ++generation.current;
    const requests = JSON.parse(signature) as {
      worldKey: string;
      admitted: boolean;
      key: string;
    }[];
    pendingPages.current.clear();
    setMorePending(new Set());
    // Forget removed/refused Worlds, including their previously paged history.
    const retained = Object.fromEntries(
      requests
        .filter((r) => r.admitted && cache.current[r.worldKey])
        .map((r) => [r.worldKey, cache.current[r.worldKey]!]),
    );
    cache.current = open && visible ? retained : {};
    setSnapshots(cache.current);
    if (!open || !visible) return;
    void (async () => {
      for (let offset = 0; offset < requests.length; offset += 4) {
        await Promise.all(
          requests.slice(offset, offset + 4).map(async (request) => {
            if (!request.admitted || cache.current[request.worldKey]?.key === request.key) return;
            const prior = cache.current[request.worldKey]?.row;
            const row = await readNotificationSources(request.worldKey, history);
            if (cut !== generation.current) return;
            // Revalidate the pages the person already opened, never the unseen backlog.
            const loaded = prior?.reminders.ok ? prior.reminders.result.page.length : 0;
            try {
              if (row.reminders.ok) {
                const cursors = new Set<string>();
                while (!row.reminders.result.isDone && row.reminders.result.page.length < loaded) {
                  if (cut !== generation.current) return;
                  const cursor = row.reminders.result.continueCursor;
                  if (!cursor || cursors.has(cursor))
                    throw new Error("Reminder pagination did not advance. Try again.");
                  cursors.add(cursor);
                  const next: CrmPage<CrmReminder> = await crmPost<CrmPage<CrmReminder>>(
                    request.worldKey,
                    {
                      action: "followUpReminders",
                      ...(history ? {} : { state: "delivered" }),
                      paginationOpts: { numItems: 20, cursor: row.reminders.result.continueCursor },
                    },
                  );
                  row.reminders = {
                    ok: true,
                    result: { ...next, page: [...row.reminders.result.page, ...next.page] },
                  };
                }
                if (prior?.reminders.ok) {
                  const fresh = new Map(
                    row.reminders.result.page.map((item) => [item.reminderId, item]),
                  );
                  const ordered: CrmReminder[] = [];
                  for (const old of prior.reminders.result.page) {
                    const item = fresh.get(old.reminderId);
                    if (item) {
                      ordered.push(item);
                      fresh.delete(old.reminderId);
                    }
                  }
                  row.reminders.result.page = [...ordered, ...fresh.values()];
                }
              }
            } catch (cause) {
              row.reminders = {
                ok: false,
                reason: cause instanceof Error ? cause.message : "Reminders could not be read.",
              };
            }
            if (cut !== generation.current) return;
            cache.current = { ...cache.current, [request.worldKey]: { key: request.key, row } };
            setSnapshots(cache.current);
          }),
        );
        if (cut !== generation.current) return;
      }
    })();
    return () => {
      generation.current++;
    };
  }, [signature, open, visible, history]);
  const rows = requests.flatMap((r) => {
    const snapshot = snapshots[r.worldKey];
    return r.admitted && snapshot?.key === r.key ? [snapshot.row] : [];
  });
  const more = async (worldKey: string) => {
    const source = rows.find((row) => row.worldKey === worldKey);
    if (
      !source?.reminders.ok ||
      source.reminders.result.isDone ||
      pendingPages.current.has(worldKey)
    )
      return;
    const cut = generation.current;
    pendingPages.current.add(worldKey);
    setMorePending(new Set(pendingPages.current));
    try {
      const next: CrmPage<CrmReminder> = await crmPost<CrmPage<CrmReminder>>(worldKey, {
        action: "followUpReminders",
        ...(history ? {} : { state: "delivered" }),
        paginationOpts: { numItems: 20, cursor: source.reminders.result.continueCursor },
      });
      if (cut !== generation.current) return;
      const prior = cache.current[worldKey];
      if (!prior?.row.reminders.ok) return;
      const merged = new Map(
        prior.row.reminders.result.page.map((item) => [item.reminderId, item]),
      );
      for (const item of next.page) merged.set(item.reminderId, item);
      cache.current = {
        ...cache.current,
        [worldKey]: {
          ...prior,
          row: {
            ...prior.row,
            reminders: { ok: true, result: { ...next, page: [...merged.values()] } },
          },
        },
      };
      setSnapshots(cache.current);
    } finally {
      if (cut === generation.current) {
        pendingPages.current.delete(worldKey);
        setMorePending(new Set(pendingPages.current));
      }
    }
  };
  return {
    rows: open && visible ? rows : [],
    loading:
      open && visible && requests.some((r) => r.admitted && snapshots[r.worldKey]?.key !== r.key),
    refresh,
    more,
    morePending,
  };
}
